//import '../app.css';

import type { Schedule, ToolbarActionArgs, ResourcesModel, EventRenderedArgs, ReturnType, CellClickEventArgs, DragEventArgs, ActionEventArgs, PopupOpenEventArgs, SelectEventArgs, RenderCellEventArgs } from '@syncfusion/ej2-schedule';
import type { ChangedEventArgs, DatePicker } from '@syncfusion/ej2-calendars';
import { Predicate, Query } from '@syncfusion/ej2-data';
import { DIALOG_ID } from "./scheduleCommon";
import { parseLocalDate, programCounterKey } from "./scheduleDate";
import { add, addDays, addMonths, startOfDay, subDays } from 'date-fns';
import { Dialog } from '@syncfusion/ej2-popups';
import { get } from 'lodash';


export async function schedule(appendTo: string, dateDto: string, resources: ResourcesModel[], datasource: any[], programCounters: { [key: string]: ProgramCounter }) {

    let roomCount = 0;

    (resources[1].dataSource as any).forEach((r: any) => {
        if (r.RoomType !== "Programs" && r.RoomType !== r.Name) {
            roomCount++;
        }
    });

    await import('@syncfusion/ej2-base/styles/bootstrap5.3.css');
    await import('@syncfusion/ej2-buttons/styles/bootstrap5.3.css');
    await import('@syncfusion/ej2-calendars/styles/bootstrap5.3.css');
    await import('@syncfusion/ej2-dropdowns/styles/bootstrap5.3.css');
    await import('@syncfusion/ej2-inputs/styles/bootstrap5.3.css');
    await import('@syncfusion/ej2-lists/styles/bootstrap5.3.css');
    await import('@syncfusion/ej2-navigations/styles/bootstrap5.3.css');
    await import('@syncfusion/ej2-popups/styles/bootstrap5.3.css');
    await import('@syncfusion/ej2-splitbuttons/styles/bootstrap5.3.css');
    await import('@syncfusion/ej2-schedule/styles/bootstrap5.3.css');

    await import('../loadSyncfusion');

    const { Schedule, Day, Week, WorkWeek, Month, TimelineViews, Agenda } = await import('@syncfusion/ej2-schedule');
    const { DatePicker } = await import('@syncfusion/ej2-calendars');

    Schedule.Inject(Day, Week, WorkWeek, Month, Agenda, TimelineViews);

    const { format } = await import('date-fns');
    const htmx = await import('htmx.org');

    let sharedState: {
        datePicker: DatePicker | null,
        setByButton: boolean
    } = {
        datePicker: null,
        setByButton: false
    };

    // Get today's date and one month from today. dateDto is the Bangkok business date as yyyy-MM-dd; parse
    // it to a LOCAL midnight so the window anchors to that calendar day regardless of the viewer's timezone.
    let currentView = 'Month';
    let today = parseLocalDate(dateDto);
    const nextEndDate = getNextEndDate(today);


    if (sharedState.datePicker) {
        today = sharedState.datePicker.value as Date;
    }

    let scheduleObj: Schedule = new Schedule({
        height: 'calc(100vh - 3.5rem)',
        //width: '100%',
        currentView: 'TimelineDay',
        enableAdaptiveUI: false,
        rowAutoHeight: true,
        views: [
            {
                option: 'TimelineDay',
                displayName: 'Custom View',
                interval: nextEndDate.daysBetween,
                dateFormat: 'dd MMM',
                timeScale: { enable: true, interval: 1440, slotCount: 1 }, // Disable the time scale to show only days,
                maxEventsPerRow: 10,
                headerRows: [
                    {
                        option: "Date"
                    },
                    {
                        option: "Hour"
                    }
                ]
            }
        ],
        showTimeIndicator: false,
        allowMultiCellSelection: true,
        selectedDate: today,
        group: {
            resources: ['Room Type', 'Room'],
            enableCompactView: false
        },
        quickInfoOnSelectionEnd: false,
        //showQuickInfo: false,
        resources: resources,
        dataBound: onDataBound,
        renderCell: onRenderCell,
        toolbarItems: [
            {
                text: 'Date Range',
                template: '<input type="text" id="date-picker" />'
            },
            {
                text: 'Today',
                template: `<button class="btn btn-outline-contrast" onclick="window.salesSchedule.setDatePeriod('Today')">Today</button>`
            },
            {
                text: 'Week',
                template: `<button class="btn btn-outline-contrast" onclick="window.salesSchedule.setDatePeriod('Week')">Week</button>`
            },
            {
                text: 'Month',
                template: `<button class="btn btn-outline-contrast" onclick="window.salesSchedule.setDatePeriod('Month')">Month</button>`
            },
            {
                text: 'Refresh',
                template: `<button class="btn btn-outline-contrast" onclick="window.salesSchedule.refresh()">Refresh</button>`
            },
            {
                text: 'Key',
                template: `<button class="btn btn-outline-contrast" data-bs-toggle="modal" data-bs-target="#colorKeyModal">Key</button>`
            },
            {
                text: 'HRM',
                template: `<button class="btn btn-outline-contrast" id="hrm-status-btn" onclick="window.salesSchedule.showHrmStatus()">
                    <i class="fas fa-heartbeat me-1"></i>HRM
                    <span id="hrm-loading-spinner" class="spinner-border spinner-border-sm ms-1" role="status" style="width: 1rem; height: 1rem;"><span class="visually-hidden">Loading...</span></span>
                    <span id="hrm-badges-container" style="display:none;">
                        <span id="hrm-due-badge" class="badge bg-danger ms-1" title="Due Today">0</span>
                        <span id="hrm-out-badge" class="badge bg-primary ms-1" title="Currently Out">0</span>
                        <span id="hrm-returned-badge" class="badge bg-success ms-1" title="Returned Today">0</span>
                    </span>
                </button>`
            },
            {
                text: 'Create',
                align: 'Right',
                template: `<button class="btn btn-outline-contrast" onclick="window.salesSchedule.createNew()">Create</button>`
            },
            //{
            //    text: 'Quarter',
            //    template: `<button class="e-btn e-flat e-primary" onclick="window.salesSchedule.setDatePeriod('Quarter')">Quarter</button>`
            //},
            //{
            //    text: 'Year',
            //    template: `<button class="e-btn e-flat e-primary" onclick="window.salesSchedule.setDatePeriod('Year')">Year</button>`
            //},
            //{
            //    text: 'Scroll to Today',
            //    template: `<button class="e-btn e-flat e-primary" onclick="document.querySelector('#schedule_container .e-content-wrap').scrollLeft = 0">Scroll Back</button>`
            //}
        ],
        actionComplete: onActionComplete,
        eventSettings: {
            dataSource: datasource,
            fields: {
                id: 'Id',
                subject: { name: 'Subject' },
                startTime: { name: 'Start' },
                endTime: { name: 'End' },
                isAllDay: { name: 'IsAllDay' },
                location: { name: 'Name' }
            },
            ignoreWhitespace: true,
            enableMaxHeight: true,

        },
        allowSwiping: false,
        allowMultiRowSelection: true,
        editorTemplate: '#EventEditorTemplate',
        popupOpen: onCustomPopupOpen,
        actionBegin: function (args: ActionEventArgs) {
            if (args.requestType === 'eventCreate' || args.requestType === 'eventChange') {

                document.getElementById("sale_booking_save").click();
            }
        },
        editorHeaderTemplate: function (data: any) {
            return `
            <div class="quick-info-header">
                <div class="header-content">
                    <div class="event-title">
                        <div style="padding: 1em; margin: 0 auto;">
                            <div class="spinner-border" role="status" style="width: 5em; height: 5em;">
                                <span class="visually-hidden">Loading</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>`;
        },

        eventRendered: function (args) {
            const now = new Date().getTime();
            const isFutureBooking = args.data.Start.getTime() > now;
            const isPastBooking = args.data.End.getTime() < now;
            const isInHouse = args.data.Start.getTime() < now && now < args.data.End.getTime();

            // Handle title formatting (unchanged)
            let title = args.data.ReservationNumber ? '#' + args.data.ReservationNumber + ' ' : '';
            if (args.data.RoomType !== 'Offsite' && isFutureBooking && !args.data.IsRoomTypeLocationId) {
                title += '(A) ';
            }
            title += args.data.Subject;
            if (args.data.Name) title += "; " + args.data.Name;
            args.element.title = title;

            const subjectEle = args.element.querySelector('.e-subject');
            if (args.data.HasError) {
                title = '(E) ' + title;
                if (args.data.ErrorMessage) {
                    title += ' - Error: ' + args.data.ErrorMessage;
                }
                subjectEle.textContent = '(E) ' + subjectEle.textContent;
                const errorIcon = document.createElement('span');
                errorIcon.classList.add('fas', 'fa-exclamation-triangle', 'text-danger', 'ms-2');
                errorIcon.style.backgroundColor = '#fff';
                errorIcon.style.padding = '0.15em';
                errorIcon.style.borderRadius = '50%';
                errorIcon.style.marginRight = '0.25em';
                if (args.data.ErrorMessage) {
                    errorIcon.title = args.data.ErrorMessage;
                }
                subjectEle.insertBefore(errorIcon, subjectEle.firstChild);
            } else {
                subjectEle.textContent = title;
            }
            args.element.title = title;

            // Apply background colors
            if (args.data.IsNoShowRoom) {
                args.element.style.backgroundColor = '#000';
                return;
            }

            // Add DNR check here
            if (args.data.Subject?.indexOf("DNR") >= 0) {
                args.element.style.backgroundColor = 'var(--dnr-active)';
                return;
            }

            // Existing color logic for other statuses
            if (isPastBooking) {
                // Checked out
                args.element.style.backgroundColor = args.data.IsFullyPaid
                    ? 'var(--gold-active)'   // Fully paid
                    : 'var(--brown-active)'; // Not fully paid
            } else if (isInHouse) {
                // In-house
                args.element.style.backgroundColor = args.data.IsFullyPaid
                    ? 'var(--green-active)'   // Fully paid
                    : 'var(--purple-active)'; // Not fully paid
            } else {
                // Future booking
                if (args.data.IsFullyPaid) {
                    args.element.style.backgroundColor = 'var(--blue-active)'; // Fully paid
                } else if (args.data.HasPayments) {
                    args.element.style.backgroundColor = 'var(--red-active)';  // Partial payments
                } else {
                    args.element.style.backgroundColor = 'var(--grey-active)'; // No payments
                }
            }

            if (args.data.IsTemporary === true) {
                args.element.style.backgroundColor = 'var(--amber-active)'; // Temp hold
            }
        },
        cellClick: function (args: CellClickEventArgs) {
            // dont allow single cell click
            args.cancel = true;
        },

        select: function (args: SelectEventArgs) {
            if (args.requestType === 'eventSelect') {
                return;
            }

            // only open the quick info popup if the user is left clicking
            if (args.event && (args.event as MouseEvent).button !== 1) {
                if ((args.event as MouseEvent).button !== 1) {
                    scheduleObj.openQuickInfoPopup(args.data);
                }
            }
        },

    });

    const oldActionBegin = scheduleObj.actionBegin;

    scheduleObj.actionBegin = function (args: ActionEventArgs) {
        oldActionBegin(args);

        if (args.requestType === 'toolbarItemRendering') {
            scheduleObj.element.classList.remove('e-device');
            scheduleObj.isAdaptive = false;
        }
    }

    scheduleObj.appendTo(appendTo);

    function setDatePeriod(period: string) {
        let start = scheduleObj.selectedDate;

        let end = start;

        if (period === 'Today') {
            //const currentTotalDays = getDaysBetween((sharedState.datePicker.value as Date[])[0], (sharedState.datePicker.value as Date[])[1]);
            start = new Date();
            const nextEndDate = getNextEndDate(start);
            end = nextEndDate.endDate;
            //end = add(start, { days: 30 });

            //document.querySelector('#schedule_container .e-content-wrap').scrollLeft = 0
        } else if (period === 'Week') {
            currentView = 'Week';
            end = add(start, { days: 6 });
        } else if (period === 'Month') {
            currentView = 'Month';
            end = add(start, { months: 1 });
        } else if (period === 'Quarter') {
            end = add(start, { months: 3 });
        } else if (period === 'Year') {
            end = add(start, { years: 1 });
        }

        sharedState.setByButton = true;


        sharedState.datePicker.value = start;

        scheduleObj.selectedDate = start;
        (scheduleObj.views[0] as any).interval = getDaysBetween(start, end);
        scheduleObj.refresh();

    }

    function createNew() {
        htmx.ajax('GET', '/sales/editPopupV2', {
            target: '#dlgContent',
            headers: {
                'HX-Component': 'true'
            }
        });

        //const createPopup = document.getElementById('schedule-dialog');
        //var ej2 = (createPopup as any).ej2_instances[0] as Dialog;
        //ej2.show(true);
    }

    // Function to handle date range changes
    function onDateChange(args: ChangedEventArgs): void {
        if (args.value) {
            const startDate = args.value as Date;
            const nextEndDate = getNextEndDate(startDate);
            //sharedState.setByButton = true;
            scheduleObj.selectedDate = startDate;
            (scheduleObj.views[0] as any).interval = nextEndDate.daysBetween;
            //scheduleObj.refresh();
        }


        //if (args.startDate && args.endDate) {
        //    scheduleObj.selectedDate = args.startDate;
        //    (scheduleObj.views[0] as any).interval = getDaysBetween(args.startDate, args.endDate);

        //    //if (sharedState.setByButton) {
        //        scheduleObj.refresh();
        //        sharedState.setByButton = false;
        //    //}
        //    //scheduleObj.changeDate(args.startDate);
        //    //scheduleObj.changeView(scheduleObj.views[0] as any)
        //}
    }

    function getNextEndDate(startDate: Date) {
        if (currentView === "Month") {
            const endDate = subDays(addMonths(startDate, 1), 1);
            return {
                endDate: endDate,
                daysBetween: getDaysBetween(startDate, endDate)
            }
        }

        if (currentView === "Week") {
            const endDate = addDays(startDate, 6);
            return {
                endDate: endDate,
                daysBetween: getDaysBetween(startDate, endDate)
            }
        }

        throw new Error("Invalid view");
    }

    // Function to reinitialize the DatePicker in the toolbar
    function reinitializeDatePicker(startDate: Date, endDate: Date): void {
        sharedState.datePicker = new DatePicker({
            value: startDate,
            change: onDateChange,
            showClearButton: false,
            allowEdit: false
        });
        sharedState.datePicker.appendTo('#date-picker');
    }

    // Function to calculate the number of days between two dates
    function getDaysBetween(startDate: Date, endDate: Date): number {
        const oneDay = 24 * 60 * 60 * 1000;
        return Math.round((endDate.getTime() - startDate.getTime()) / oneDay) + 1; // Include the end date
    }

    // Function to customize header cells after data is bound
    function onDataBound(args: ReturnType): void {
        const headerCells = document.querySelectorAll('.e-timeline-day-view .e-date-header-wrap .e-header-cells');

        headerCells.forEach(cell => {
            const date = new Date(parseInt(cell.getAttribute('data-date')!));
            const monthShortName = date.toLocaleDateString('en-US', { month: 'short' });
            const dayNumber = date.getDate();
            const dayName = date.toLocaleDateString('en-US', { weekday: 'short' });

            let roomBookingsCount = 0;

            datasource.forEach((booking: any) => {
                const bookingStart = startOfDay(new Date(booking.Start));
                const bookingEnd = startOfDay(new Date(booking.End));

                if (date >= bookingStart && date < bookingEnd) {
                    // Exclude temporary reservations from the occupied-room count,
                    // but keep the total room count (roomCount) unchanged.
                    if (booking.RoomType !== "Offsite" && booking.IsTemporary !== true) {
                        roomBookingsCount++;
                    }
                }
            });

            let offsitePrograms = 0;
            let onsitePrograms = 0;
            let totalPrograms = 0;

            // find program counters by current date
            const programCounter = programCounters[programCounterKey(date)];

            if (programCounter) {
                offsitePrograms = programCounter.Offsite;
                onsitePrograms = programCounter.Onsite;
                totalPrograms = programCounter.Total;
            }

            const occupancy = Math.round((roomBookingsCount / roomCount) * 100);

            cell.classList.add("phuketfit-schedule-header");

            cell.innerHTML = `
                <div>
                    <div>${monthShortName} ${dayNumber}</div>
                    <div>${dayName}</div>
                </div>
                <div class="occupancy-header">
                <div>${roomBookingsCount}/${roomCount}</div>
                <div>${occupancy}%</div>
                </div>
                <div class="occupancy-header programs-occupancy-header">
                    <div>
                        Onsite: ${onsitePrograms}
                    </div>
                    <div>
                        Offsite: ${offsitePrograms}
                    </div>
                </div>`;
        });

        scheduleObj.refreshLayout();
    }

    // Render count badges for Programs rows and the Programs parent header via Syncfusion renderCell
    function onRenderCell(args: RenderCellEventArgs) {
        try {
            if (!args || !args.element) return;
            // Only target timeline work cells (content grid)
            if (!args.element.classList.contains('e-work-cells')) return;
 
            const giAttr = args.element.getAttribute('data-group-index');
            if (giAttr == null) return;
            const groupIndex = parseInt(giAttr, 10);
            if (isNaN(groupIndex)) return;
 
            const resourceLevels = (scheduleObj as any).resourceBase?.lastResourceLevel || [];
            const level = resourceLevels.find((lv: any) => lv.groupIndex === groupIndex);
            const r = level?.resourceData;
            if (!r) return;
 
            const dateAttr = args.element.getAttribute('data-date');
            if (!dateAttr) return;
            const cellDate = new Date(Number(dateAttr));
            const pc = (programCounters as any)[programCounterKey(cellDate)] as { Total?: number; ProgramTypes?: Record<string, number> } | undefined;
 
            ensureProgramCountsStyles();
 
            // Detect the parent "Programs" row: Name === "Programs" and no RoomType field
            const nameVal = String(((r as any).Name ?? '')).trim().toLowerCase();
            const hasRoomTypeField = (r as any).RoomType !== undefined;
            const isProgramsParent = nameVal === 'programs' && !hasRoomTypeField;
 
            let valueText = '';
 
            if (isProgramsParent) {
                const total = pc?.Total ?? 0;
                valueText = total > 0 ? String(total) : '';
            } else if ((r as any).RoomType === 'Programs') {
                // Child program rows (e.g., TotalFitness, WeightLoss)
                const resourceId: string = (((r as any).ResourceId) ?? '') + '';
                const resourceName: string = (((r as any).Name) ?? '') + '';
                let programKey: string | null = null;
 
                if (/TotalFitness/i.test(resourceId) || /Total\s*Fitness|Fitness/i.test(resourceName)) programKey = 'Total Fitness';
                else if (/WeightLoss/i.test(resourceId) || /Weight\s*Loss/i.test(resourceName)) programKey = 'Weight Loss';
 
                const count = (programKey && pc?.ProgramTypes && pc.ProgramTypes[programKey] != null) ? pc.ProgramTypes[programKey]! : 0;
                valueText = count > 0 ? String(count) : '';
            } else {
                // Not a Programs-related row
                return;
            }
 
            args.element.classList.add('program-count-cell');
            let badge = args.element.querySelector('span.program-count-badge') as HTMLElement | null;
            if (!badge) {
                badge = document.createElement('span');
                badge.className = 'program-count-badge';
                args.element.appendChild(badge);
            }
            // Keep muted look; hide zero
            badge.textContent = valueText;
        } catch {
            // no-op
        }
    }

    // Render count badges in each work cell for "Programs" resource rows (e.g., Weight Loss, Total Fitness)
    function renderProgramRowProgramCounts() {
        try {
            const resourceLevels = (scheduleObj as any).resourceBase?.lastResourceLevel || [];
            if (!resourceLevels.length) return;

            ensureProgramCountsStyles();

            // Use the schedule's current dataSource (may be refreshed)
            const data = (scheduleObj.eventSettings?.dataSource as any[]) || [];

            resourceLevels.forEach((level: any) => {
                const r = level?.resourceData;
                if (!r || r.RoomType !== 'Programs') return;

                const groupIndex = level.groupIndex;
                const locationId = r.LocationId;

                const cells = scheduleObj.element.querySelectorAll(`.e-content-wrap .e-content-table .e-work-cells[data-group-index="${groupIndex}"]`);
                cells.forEach((cell: HTMLElement) => {
                    const dateMsAttr = cell.getAttribute('data-date');
                    if (!dateMsAttr) return;
                    const date = startOfDay(new Date(parseInt(dateMsAttr)));

                    let count = 0;
                    for (const b of data) {
                        try {
                            if (!b) continue;
                            if (b.RoomType !== 'Programs') continue;
                            if (b.Room !== locationId) continue;

                            const bStart = startOfDay(new Date(b.Start));
                            const bEnd = startOfDay(new Date(b.End));
                            if (date >= bStart && date < bEnd) {
                                count++;
                            }
                        } catch { /* skip bad rows */ }
                    }

                    cell.classList.add('program-count-cell');

                    const badgeId = `pc-${groupIndex}-${date.getTime()}`;
                    let badge = cell.querySelector(`span[data-badge-id="${badgeId}"]`) as HTMLElement | null;
                    if (!badge) {
                        badge = document.createElement('span');
                        badge.setAttribute('data-badge-id', badgeId);
                        badge.className = 'program-count-badge';
                        cell.appendChild(badge);
                    }
                    badge!.textContent = count > 0 ? String(count) : '';
                });
            });
        } catch {
            // no-op
        }
    }

    function ensureProgramCountsStyles() {
        if (document.getElementById('program-counts-style')) return;
        const style = document.createElement('style');
        style.id = 'program-counts-style';
        style.textContent = `
            .program-count-cell { position: relative; }
            .program-count-badge {
                    position: absolute;
                    left: 50%;
                    top: 50%;
                    transform: translate(-50%, -50%);
                    font-size: 0.72rem;
                    line-height: 1rem;
                    color: #6c757d;
                    background: transparent;
                    border-radius: 9999px;
                    padding: 0 6px;
                    pointer-events: none;
                    opacity: 0.8;
                }
        `;
        document.head.appendChild(style);
    }

    // Function to initialize the DatePicker in the toolbar
    function onActionComplete(args: ToolbarActionArgs): void {
        if (args.requestType === 'toolBarItemRendered') {
            const now = sharedState?.datePicker?.value || today;
            const nextEndDate = getNextEndDate(now);
            reinitializeDatePicker(
                now,
                nextEndDate.endDate);
            
            // Load HRM status after toolbar is rendered
            loadHrmStatus();
        }
    }

    async function onCustomPopupOpen(args: PopupOpenEventArgs) {
        if (args.type == "QuickInfo") {
            // Force to open Detail pop (Editor), the dialog need information from Editor popup
            args.cancel = true;
            var currentAction: 'Add' | 'Save';
            currentAction = args.target.classList.contains("e-work-cells") ? 'Add' : 'Save';
            scheduleObj.openEditor(args.data, currentAction);
            return;
        }
        if (args.type === 'Editor') {
            args.cancel = true;

            document.getElementById('schedule-loader').classList.remove('hidden');

            // args.target == null : create, if not null: edit existing booking
            if (args.target != null) {

                // open SalePopupComponent for Editor content.
                const htmx = (window as any).htmx;
                const url = args.data.ReservationId
                    ? `/sales/editPopup?reservationId=${args.data.ReservationId}`
                    : `/sales/editPopup?bookingId=${args.data.Id}`;
                await htmx.ajax('GET', url, {
                    target: '#dlgContent',
                    headers: {
                        'HX-Component': 'true'
                    }
                });
            }
            else {
                var start = (new Date(args.data.Start)).toDateString();
                var end = (new Date(args.data.End)).toDateString();

                start = format(start, 'yyyy-MM-dd')
                end = format(end, 'yyyy-MM-dd')

                var query = '';
                if (args.data.Start) {
                    query += 'startDate=' + start + '&'
                }
                if (args.data.End) {
                    query += 'endDate=' + end + '&'
                }

                if (args.data.RoomType && args.data.RoomType !== 'Offsite') {
                    query += 'roomType=' + args.data.RoomType + '&'
                }
                if (args.data.Resources && args.data.Resources !== 'Offsite') {
                    query += 'resources=' + args.data.Resources + '&'
                }

                await htmx.ajax('GET', '/sales/editPopupV2?' + query, {
                    target: '#dlgContent',
                    headers: {
                        'HX-Component': 'true'
                    }
                });

            }

            document.getElementById('schedule-loader').classList.add('hidden');

            //const createPopup = document.getElementById(DIALOG_ID) as HTMLElement;
            //var element: any = createPopup;
            //var ej2 = element.ej2_instances[0] as Dialog;
            //ej2.show(true);

            return;
        }

    }

    async function loadHrmStatus() {
        try {
            const spinner = document.getElementById('hrm-loading-spinner');
            const badgesContainer = document.getElementById('hrm-badges-container');
            
            // Show spinner, hide badges while loading
            if (spinner) spinner.style.display = 'inline-block';
            if (badgesContainer) badgesContainer.style.display = 'none';
            
            const response = await fetch('/api/hrm/schedule-status');
            if (response.ok) {
                const data = await response.json();
                
                // Update the 3 colored badges (always show all 3)
                const dueBadge = document.getElementById('hrm-due-badge');
                const outBadge = document.getElementById('hrm-out-badge');
                const returnedBadge = document.getElementById('hrm-returned-badge');
                
                const dueCount = data.dueTodayCount || 0;
                const outCount = data.outCount || 0;
                const returnedCount = data.returnedTodayCount || 0;
                
                if (dueBadge) {
                    dueBadge.textContent = dueCount.toString();
                }
                
                if (outBadge) {
                    outBadge.textContent = outCount.toString();
                }
                
                if (returnedBadge) {
                    returnedBadge.textContent = returnedCount.toString();
                }
                
                // Store the data for the modal
                (window as any).__hrmScheduleStatus = data;
            }
            
            // Hide spinner, show badges
            if (spinner) spinner.style.display = 'none';
            if (badgesContainer) badgesContainer.style.display = 'inline';
        } catch (e) {
            console.error('Failed to load HRM status:', e);
            // Hide spinner on error too
            const spinner = document.getElementById('hrm-loading-spinner');
            const badgesContainer = document.getElementById('hrm-badges-container');
            if (spinner) spinner.style.display = 'none';
            if (badgesContainer) badgesContainer.style.display = 'inline';
        }
    }

    function showHrmStatus() {
        const modal = document.getElementById('hrmStatusModal');
        if (modal) {
            const bsModal = new (window as any).bootstrap.Modal(modal);
            
            // Update modal content with current data
            const data = (window as any).__hrmScheduleStatus;
            if (data) {
                updateHrmModalContent(data);
            }
            
            bsModal.show();
        }
    }

    function updateHrmModalContent(data: any) {
        // Update summary counts
        const outCountEl = document.getElementById('hrm-out-count');
        const dueTodayCountEl = document.getElementById('hrm-due-today-count');
        const returnedTodayCountEl = document.getElementById('hrm-returned-today-count');
        
        if (outCountEl) outCountEl.textContent = data.outCount || '0';
        if (dueTodayCountEl) dueTodayCountEl.textContent = data.dueTodayCount || '0';
        if (returnedTodayCountEl) returnedTodayCountEl.textContent = data.returnedTodayCount || '0';
        
        // Update tables
        updateHrmTable('hrm-out-table', data.outLoans || [], false);
        updateHrmTable('hrm-due-today-table', data.dueTodayLoans || [], false);
        updateHrmTable('hrm-returned-today-table', data.returnedTodayLoans || [], true);
    }

    function updateHrmTable(tableId: string, loans: any[], showCheckInTime: boolean) {
        const tbody = document.querySelector(`#${tableId} tbody`);
        if (!tbody) return;
        
        if (loans.length === 0) {
            tbody.innerHTML = `<tr><td colspan="${showCheckInTime ? 4 : 4}" class="text-center text-muted">No items</td></tr>`;
            return;
        }
        
        // Get today's date for comparison (date only, no time)
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        tbody.innerHTML = loans.map((loan: any) => {
            const dueDate = loan.effectiveDueDate
                ? new Date(loan.effectiveDueDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })
                : (loan.dueDateFollowsProgram ? 'Program End' : '-');
            
            const checkInTime = loan.checkInUtc
                ? new Date(loan.checkInUtc).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
                : '';
            
            // Highlight red if overdue OR due today
            let isDueOrOverdue = loan.isOverdue;
            if (!isDueOrOverdue && loan.effectiveDueDate) {
                const effectiveDate = new Date(loan.effectiveDueDate);
                effectiveDate.setHours(0, 0, 0, 0);
                isDueOrOverdue = effectiveDate.getTime() <= today.getTime();
            }
            
            const rowClass = isDueOrOverdue ? 'table-danger' : '';
            const reservationLink = loan.reservationId
                ? `<a href="#" onclick="window.salesSchedule.openReservation('${loan.reservationId}'); return false;">#${loan.reservationNumber || '?'}</a>`
                : '-';
            
            if (showCheckInTime) {
                return `<tr class="${rowClass}">
                    <td>${loan.deviceNumber}</td>
                    <td>${loan.guestName}</td>
                    <td>${reservationLink}</td>
                    <td>${checkInTime}</td>
                </tr>`;
            }
            
            return `<tr class="${rowClass}">
                <td>${loan.deviceNumber}</td>
                <td>${loan.guestName}</td>
                <td>${reservationLink}</td>
                <td>${dueDate}</td>
            </tr>`;
        }).join('');
    }

    async function openReservation(reservationId: string) {
        const htmx = (window as any).htmx;
        await htmx.ajax('GET', `/sales/editPopup?reservationId=${reservationId}`, {
            target: '#dlgContent',
            headers: {
                'HX-Component': 'true'
            }
        });
    }

    return {
        setDatePeriod: setDatePeriod,
        createNew: createNew,
        showHrmStatus: showHrmStatus,
        openReservation: openReservation,
        refreshHrmStatus: loadHrmStatus,
        refresh: async function (status: 'quote' | 'reserved') {
            if (!status || status === "reserved") {
                const response = await fetch(`/reservations-bookings`, {
                    method: 'GET',
                });

                const data = await response.json();

                scheduleObj.eventSettings.dataSource = data;
            } else {
                window.location.href = '/quotes';

                await new Promise(() => { }); // Inline never-completing promise
            }
        },
        closed: function () {
            //
        }
    };
}

export interface ProgramCounter {
    Offsite: number;
    Onsite: number;
    Total: number;
    ProgramTypes: { [key: string]: number };
}